import React from 'react';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { ClaimEventType, DebtorClaimStage } from '../../../api/client';
import DebtorClaimEventIcon from '../../debtorClaimEvents/presentation/DebtorClaimEventIcon';
import DebtorClaimEventDetails from './DebtorClaimEventDetails';
import TooltipContent from '../presentation/TooltipContent';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { actionCreators as DebtorLeapClaimsStoreActionCreators } from '../../../store/DebtorLeapClaimsStore';
import { colors } from './../../../colors';
import { Badge } from '@mui/material';

interface PropsFromDispatch {
  downloadLeapReceipt: typeof DebtorLeapClaimsStoreActionCreators.downloadLeapReceipt;
  downloadLeapClaimCredit: typeof DebtorLeapClaimsStoreActionCreators.downloadLeapClaimCredit;
  downloadLeapInvoice: typeof DebtorLeapClaimsStoreActionCreators.downloadLeapInvoice;
  downloadLeapDebtCollectionWarning: typeof DebtorLeapClaimsStoreActionCreators.downloadLeapDebtCollectionWarning;
  downloadLeapDocumentById: typeof DebtorLeapClaimsStoreActionCreators.downloadLeapDocumentById;
}

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: colors.marble,
    color: colors.black,
    boxShadow: '0px 1px 5px 0px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.12)',
    fontSize: '0.875rem',
    fontWeight: '500',
    maxWidth: '600px',
    marginTop: '4px',
    padding: '10px'
  },
}));

interface InjectedProps {
  claimSystem: string;
  debtorId: string;
  eventId: string;
  referenceNumber: string;
  eventType: ClaimEventType;
  eventName: string;
  eventTooltip: { [key: string]: string; } | undefined;
  documentId: string | undefined;
  filename: string | undefined;
  documentStage: DebtorClaimStage | undefined;
}

type ComponentProps = PropsFromDispatch & InjectedProps;

class DebtorClaimEventDetailsTooltip extends React.Component<ComponentProps> {

  downloadClaimReceiptPDF = () => {
    this.props.downloadLeapReceipt(this.props.referenceNumber);
  }
  downloadClaimDocument = () => {
    this.props.documentId && this.props.documentId.length > 0 ? this.props.downloadLeapDocumentById(this.props.documentId, this.props.filename!) : this.props.downloadLeapInvoice(this.props.referenceNumber);
  }
  downloadLeapClaimCreditPDF = () => {
    this.props.downloadLeapClaimCredit(this.props.referenceNumber);
  }
  downloadLeapDebtCollectionWarningPDF = () => {
    this.props.downloadLeapDebtCollectionWarning(this.props.referenceNumber);
  }

  public render() {

    const disableHover = (
      ![
        ClaimEventType.PaymentInformationSentBySms,
        ClaimEventType.PaymentInformationSentByEmail,
        ClaimEventType.ObjectionRegistered
      ].includes(this.props.eventType!)

      && !this.props.eventTooltip
    );

    const canDownloadInvoice = (
      this.props.documentStage === DebtorClaimStage.Invoice
      && [
        ClaimEventType.Letter,
        ClaimEventType.ElectronicInvoice,
        ClaimEventType.InvoiceSmsReceived,
        ClaimEventType.InvoiceSmsNotReceived,
        ClaimEventType.DigiPostInvoice,
        ClaimEventType.SmsSent
      ].includes(this.props.eventType!)
    );

    const receiptDownloadable = this.props.eventType! === ClaimEventType.FullyPaidReceiptAvailable;
    const documentDownloadable = (this.props.documentId && this.props.documentId.length > 0) || canDownloadInvoice;
    const downloadType = receiptDownloadable
      ? this.downloadClaimReceiptPDF
      : documentDownloadable
        ? this.downloadClaimDocument
        : undefined;

    return (
      <HtmlTooltip
        title={
          this.props.eventTooltip && this.props.eventTooltip !== undefined ?
          Object.keys(this.props.eventTooltip!).map((key) => (<TooltipContent key={key} header={key} content={this.props.eventTooltip![key]} /> )) :
          <DebtorClaimEventDetails id={this.props.eventId} />
        }
        disableHoverListener={disableHover}
        placement="bottom-start"
        enterDelay={100}
        leaveDelay={200}
        PopperProps={{
          style: {
            opacity: 1
          }
        }}
      >
        
        <Box onClick={downloadType} sx={{
            display: 'inline',
            ...(( documentDownloadable || receiptDownloadable ) && { cursor: 'pointer' })
        }}>
          <Badge anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            sx={{
            '& .MuiBadge-badge': {
              backgroundColor: 'transparent',
              color: colors.primary.bluezodiac,
              left: '50%',
              bottom: '-25%',
              fontSize: '11px',
              whiteSpace: 'nowrap'
            },
            }} badgeContent={ documentDownloadable || receiptDownloadable ? <u>Last ned</u> : null }>
            <DebtorClaimEventIcon eventType={this.props.eventType} />
          </Badge>
        </Box>
        
      </HtmlTooltip>
    );
  }
}

const mapDispatchToProps: PropsFromDispatch = {
  downloadLeapReceipt: DebtorLeapClaimsStoreActionCreators.downloadLeapReceipt,
  downloadLeapClaimCredit: DebtorLeapClaimsStoreActionCreators.downloadLeapClaimCredit,
  downloadLeapInvoice: DebtorLeapClaimsStoreActionCreators.downloadLeapInvoice,
  downloadLeapDebtCollectionWarning: DebtorLeapClaimsStoreActionCreators.downloadLeapDebtCollectionWarning,
  downloadLeapDocumentById: DebtorLeapClaimsStoreActionCreators.downloadLeapDocumentById,
};

export default compose(
  connect(null, mapDispatchToProps)
)(DebtorClaimEventDetailsTooltip);
